import React, { FC } from 'react';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';

import { IShiftOfferCalendarDTO } from 'src/api/dtos';
import ShiftOfferRepository from 'src/api/repositories/ess/v1/shiftOffer.repository';
import { IError } from 'src/api/types';
import { Modal, Status, SuitcaseIcon } from 'src/shared/components';
import { DAY_COMMA_DATE_FORMAT } from 'src/shared/constants';
import { PermissionsEnums, ShiftOfferRecipientStatusEnum } from 'src/shared/enums';
import { ComponentWithPermissions } from 'src/shared/hocs';
import { useTranslate } from 'src/shared/providers';
import { getErrorMessage } from 'src/shared/utils';

import { CalendarNotesInfo, CalendarTimeInfo } from './modal-content';

interface Props {
  item: IShiftOfferCalendarDTO;
  open: boolean;
  onClose: () => void;
}

export const CalendarShiftOfferModal: FC<Props> = ({ item, open, onClose }) => {
  const { t } = useTranslate();

  const {
    shiftOfferId,
    appliesToDate,
    startDateTime,
    endDateTime,
    shiftDescription,
    ruleDescription,
    qualificationDescription,
    recipientStatus,
    note,
  } = item;

  const [acceptShiftOffer, { isLoading: isAccepting }] = ShiftOfferRepository.endpoints.useAcceptShiftOfferMutation();
  const [declineShiftOffer, { isLoading: isDeclining }] = ShiftOfferRepository.endpoints.useDeclineShiftOfferMutation();

  const loading = isAccepting || isDeclining;
  const hasExpired = moment(startDateTime).isBefore(moment());
  const isPendingApproval = recipientStatus === ShiftOfferRecipientStatusEnum.PENDING;
  // PendingApproval offers are read-only — employee has already accepted, manager
  // hasn't yet approved/rejected. Expired offers are also read-only.
  const isReadOnly = isPendingApproval || hasExpired;

  const message = moment(appliesToDate).format(DAY_COMMA_DATE_FORMAT);

  const handleAccept = async () => {
    try {
      await acceptShiftOffer({ offerId: shiftOfferId }).unwrap();
      toast.info(t('calendar.youHaveSucessfullyAcceptedTheShift'), { type: toast.TYPE.SUCCESS });
      onClose();
    } catch (error) {
      const errorMessage = getErrorMessage(error as IError, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
    }
  };

  const handleDecline = async () => {
    try {
      await declineShiftOffer({ offerId: shiftOfferId }).unwrap();
      toast.info(t('calendar.youHaveSucessfullyDeclinedTheShift'), { type: toast.TYPE.SUCCESS });
      onClose();
    } catch (error) {
      const errorMessage = getErrorMessage(error as IError, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
    }
  };

  return (
    <Modal
      type={isPendingApproval ? 'pending' : 'warning'}
      open={open}
      onClose={onClose}
      modalHeaderProps={{
        title: (
          <Stack direction="row" gap={2}>
            Shift Offer
            <Chip
              variant="filled"
              label={isPendingApproval ? t('shiftSwaps.pendingApproval') : t('calendar.offeredShift')}
              sx={{
                borderRadius: '4px',
                backgroundColor: isPendingApproval ? '#eef3fb' : '#f2eec0',
                color: isPendingApproval ? '#3c5b8b' : '#938709',
              }}
            />
          </Stack>
        ),
        message,
        startIcon: <Status size="large" variant={isPendingApproval ? 'pending' : 'warning'} />,
        onClose,
      }}
      modalFooterProps={
        isReadOnly
          ? undefined
          : {
              closeText: t('calendar.declineShift'),
              onClose: handleDecline,
              submitText: t('calendar.acceptShift'),
              onSubmit: handleAccept,
              loading,
            }
      }
    >
      <div className="ms-3 ps-3">
        {hasExpired && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('calendar.thisShiftOfferWindowHasExpired')}
          </Alert>
        )}
        {isPendingApproval && !hasExpired && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('calendar.youHaveAcceptedThisShiftOfferItIsAwaiting')}
          </Alert>
        )}
        <CalendarTimeInfo startTime={startDateTime} endTime={endDateTime} />
        <Stack direction="column" spacing={2}>
          <Stack gap={1} direction="row" alignItems="center">
            <SuitcaseIcon color="#6C757D" size={16} className="me-2" />
            <strong>Details</strong>
          </Stack>
          <Stack gap={2} direction="row" sx={{ border: '1.5px solid #e9ecef', borderRadius: 2, p: 1.5 }}>
            {ruleDescription && (
              <div>
                <span>{t('calendar.rule')}: </span>
                {ruleDescription}
              </div>
            )}
            {shiftDescription && (
              <div>
                <span>{t('calendar.shift')}: </span>
                {shiftDescription}
              </div>
            )}
            <div>
              <span>{t('calendar.qualification')}: </span>
              {qualificationDescription ?? t('global.none')}
            </div>
          </Stack>
        </Stack>
        <ComponentWithPermissions permissions={PermissionsEnums.SS_NOTES_VIEW}>
          {note && (
            <CalendarNotesInfo notes={[{ label: t('calendar.noteFromManager'), value: note }]} direction="column" />
          )}
        </ComponentWithPermissions>
      </div>
    </Modal>
  );
};
